import { Children, Fragment, isValidElement, type ReactNode } from 'react';

/**
 * Regras do seletor com busca, sem tela: o que aparece para o texto digitado, em que grupos e para
 * onde o teclado anda. A busca ignora maiúsculas e acentos, e cada palavra pode estar em qualquer
 * parte do item ("analyst 1001" acha "100140 · Sales Analyst").
 */
export interface SearchOption {
  id: string;
  label: string;
  /** Segunda linha do item (ex.: família e subfamília do cargo). */
  detail?: string;
  /** Texto a mais para a busca, que não aparece na tela. */
  searchText?: string;
  /** Título do grupo. Itens do mesmo grupo devem vir juntos. */
  group?: string;
  /** Etiqueta ao lado do item ("Fora do departamento", "Inativo"). */
  tag?: { label: string; tone: 'accent' | 'info' | 'off' };
  /** Aparece, mas não pode ser escolhido. */
  disabled?: boolean;
}

/** A partir de quantas opções o campo de seleção comum passa a aceitar a digitação. */
export const SEARCH_SELECT_MIN_OPTIONS = 8;

const plainText = (node: ReactNode): string | null => {
  const parts = Children.toArray(node);
  if (parts.some((p) => typeof p !== 'string' && typeof p !== 'number')) return null;
  return parts.join('');
};

/**
 * As opções escritas dentro do campo de seleção comum (`<option>`, `<optgroup>` e fragmentos), para
 * o seletor com busca. Devolve `null` se houver algo que não dá para ler: o campo fica como sempre foi.
 */
export function optionsFromChildren(children: ReactNode, group?: string): SearchOption[] | null {
  const options: SearchOption[] = [];
  for (const child of Children.toArray(children)) {
    // Sobra de `{valor && <option>}` com valor vazio: a caixa de seleção também a ignora.
    if (!isValidElement<Record<string, unknown>>(child)) continue;
    const props = child.props;
    if (child.type === 'option') {
      const label = (props.label as string | undefined) ?? plainText(props.children as ReactNode);
      if (label === null) return null;
      const searchText = props['data-search'] as string | undefined;
      options.push({
        id: String(props.value ?? label),
        label,
        ...(searchText ? { searchText } : {}),
        ...(group ? { group } : {}),
        ...(props.disabled ? { disabled: true } : {}),
      });
    } else if (child.type === 'optgroup' || child.type === Fragment) {
      const label = child.type === 'optgroup' ? (props.label as string) : group;
      const inner = optionsFromChildren(props.children as ReactNode, label);
      if (!inner) return null;
      options.push(...inner);
    } else {
      return null;
    }
  }
  return options;
}

const plain = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
/** Sem pontuação: "11.444.777/0001-61" e "12-3456789" acham os números guardados sem ela. */
const bare = (text: string) => text.replace(/[^\p{L}\p{N}\s]/gu, '');

/** O item combina com o texto digitado? */
export function matchesSearch(option: SearchOption, query: string): boolean {
  const words = plain(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text = plain([option.label, option.detail, option.searchText].filter(Boolean).join(' '));
  const loose = bare(text);
  return words.every((w) => text.includes(w) || (bare(w) !== '' && loose.includes(bare(w))));
}

/** Itens que aparecem para o texto digitado, na ordem recebida. */
export function filterOptions(options: readonly SearchOption[], query: string): SearchOption[] {
  return options.filter((o) => matchesSearch(o, query));
}

/** Itens em grupos, sem mudar a ordem. */
export function groupOptions(
  options: readonly SearchOption[],
): { group: string | null; options: SearchOption[] }[] {
  const groups: { group: string | null; options: SearchOption[] }[] = [];
  for (const option of options) {
    const group = option.group ?? null;
    const last = groups[groups.length - 1];
    if (last && last.group === group) last.options.push(option);
    else groups.push({ group, options: [option] });
  }
  return groups;
}

/** Próximo item que pode ser escolhido, dando a volta nas pontas; -1 se nenhum pode. */
export function nextEnabled(
  options: readonly SearchOption[],
  current: number,
  step: 1 | -1,
): number {
  const total = options.length;
  for (let i = 1; i <= total; i += 1) {
    const at = (current + step * i + total * (i + 1)) % total;
    if (at >= 0 && !options[at]?.disabled) return at;
  }
  return -1;
}
